import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Plus, FileText, Calendar, BookOpen, Mic, Building2, Users,
  Search, Sparkles, Clock, Loader2, CalendarPlus, Quote, Download,
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { notify } from '../utils/notifications';
import { AvatarImage } from '../components/AvatarImage';
import { useApp } from '../context/AppContext';
import { useFavorites } from '../hooks/useFavorites';
import { useEventData } from '../context/EventDataContext';
import { exportFile, slugify as slug } from '../utils/exportShare';
import { buildEventMarkdown, buildBibTeX, buildRIS, buildICS } from '../utils/exporters';
import type { Session, Paper, Profile, Exhibitor, FavoriteEntityType } from '@conference/shared';
import { useEventModules } from '../hooks/useEventModules';
import {
  getActiveNavigationTab,
  getLibraryNavigation,
  type ModuleNavigationTab,
} from '../utils/moduleNavigation';

const TAB_ICONS = {
  notes: FileText,
  agenda: Calendar,
  papers: BookOpen,
  speakers: Mic,
  exhibitors: Building2,
  contacts: Users,
} as const;

function EmptyFavorites({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-2">
      <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No saved {label} yet</p>
      <p className="text-xs text-center" style={{ color: 'var(--app-text-subtle)' }}>
        Bookmark items while browsing to save them here
      </p>
    </div>
  );
}

export default function LibraryScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');
  const { notes, addNote, currentEventId, authSession, isAuthSessionCurrent } = useApp();
  const { services: { eventService, sessionService, paperService, speakerService, exhibitorService, attendeeService } } = useEventData();
  const { getFavoriteIdsByType } = useFavorites();
  const { modules, isEnabled } = useEventModules();
  const tabs = getLibraryNavigation(modules);
  const activeTabEntry = getActiveNavigationTab(location.pathname, tabs);
  const activeTab = activeTabEntry?.id ?? 'contacts';
  const tabSignature = tabs.map((tab) => `${tab.id}:${tab.label}`).join('|');

  // Favorited item data
  const [favSessions, setFavSessions] = useState<Session[]>([]);
  const [favPapers, setFavPapers] = useState<Paper[]>([]);
  const [favSpeakers, setFavSpeakers] = useState<Profile[]>([]);
  const [favExhibitors, setFavExhibitors] = useState<Exhibitor[]>([]);
  const [favContactState, setFavContactState] = useState<{
    eventId: string | null;
    contacts: Profile[];
  }>({ eventId: null, contacts: [] });
  const [loadingRequestId, setLoadingRequestId] = useState<number | null>(null);
  const loadRequestSequence = useRef(0);
  const contactRequestSequence = useRef(0);
  const speakerRequestSequence = useRef(0);
  const loadingFav = loadingRequestId !== null;
  const favContacts = favContactState.eventId === currentEventId
    ? favContactState.contacts
    : [];

  useEffect(() => {
    loadRequestSequence.current += 1;
    contactRequestSequence.current += 1;
    speakerRequestSequence.current += 1;
    setLoadingRequestId(null);
    setFavContactState({ eventId: currentEventId, contacts: [] });
    setFavSessions([]);
    setFavPapers([]);
    setFavSpeakers([]);
    setFavExhibitors([]);
  }, [currentEventId, tabSignature]);

  // Load favorited items when tab changes OR when the favorite set finishes loading from the API
  useEffect(() => {
    if (activeTab === 'notes') {
      loadRequestSequence.current += 1;
      setLoadingRequestId(null);
      return;
    }
    const loadRequestId = ++loadRequestSequence.current;
    const contactRequestId = activeTab === 'contacts'
      ? ++contactRequestSequence.current
      : undefined;
    const speakerRequestId = activeTab === 'speakers'
      ? ++speakerRequestSequence.current
      : undefined;
    if (contactRequestId !== undefined) {
      setFavContactState({ eventId: currentEventId, contacts: [] });
    }
    loadFavoritedItems(
      activeTab,
      currentEventId,
      loadRequestId,
      contactRequestId,
      speakerRequestId,
    );
    return () => {
      if (loadRequestSequence.current === loadRequestId) {
        loadRequestSequence.current += 1;
        setLoadingRequestId((current) => (
          current === loadRequestId ? null : current
        ));
      }
      if (
        contactRequestId !== undefined
        && contactRequestSequence.current === contactRequestId
      ) {
        contactRequestSequence.current += 1;
      }
      if (
        speakerRequestId !== undefined
        && speakerRequestSequence.current === speakerRequestId
      ) {
        speakerRequestSequence.current += 1;
      }
    };
  }, [
    activeTab,
    authSession,
    currentEventId,
    getFavoriteIdsByType,
    isAuthSessionCurrent,
  ]);

  async function loadFavoritedItems(
    tab: string,
    eventId: string | null,
    loadRequestId: number,
    contactRequestId?: number,
    speakerRequestId?: number,
  ) {
    if (!eventId) {
      setLoadingRequestId((current) => (
        current === loadRequestId ? null : current
      ));
      if (tab === 'contacts' || tab === 'speakers') {
        notify.error('Select a conference', 'Choose an event before loading saved profiles.');
      }
      return;
    }
    setLoadingRequestId(loadRequestId);

    // allSettled so a single 404 (stale favorite) doesn't kill the whole batch
    async function fetchAll<T>(ids: string[], fetcher: (id: string) => Promise<T | null>): Promise<T[]> {
      const settled = await Promise.allSettled(ids.map(fetcher));
      const values: T[] = [];
      settled.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) values.push(result.value);
      });
      return values;
    }

    try {
      if (tab === 'agenda') {
        const ids = getFavoriteIdsByType('session');
        setFavSessions(ids.length === 0 ? [] : await fetchAll(ids, (id) => sessionService.getById(id, eventId)));
      } else if (tab === 'papers') {
        const ids = getFavoriteIdsByType('paper');
        setFavPapers(ids.length === 0 ? [] : await fetchAll(ids, (id) => paperService.getById(id, eventId)));
      } else if (tab === 'speakers') {
        const owner = authSession;
        if (!owner || !isAuthSessionCurrent(owner)) return;
        const ids = getFavoriteIdsByType('speaker');
        const speakers = ids.length === 0
          ? []
          : await fetchAll(
              ids,
              (id) => speakerService.getById(
                id,
                eventId,
                { signal: owner.signal },
              ),
            );
        if (
          speakerRequestId === speakerRequestSequence.current
          && isAuthSessionCurrent(owner)
        ) {
          setFavSpeakers(speakers);
        }
      } else if (tab === 'exhibitors') {
        const ids = getFavoriteIdsByType('exhibitor');
        setFavExhibitors(ids.length === 0 ? [] : await fetchAll(ids, (id) => exhibitorService.getById(id, eventId)));
      } else if (tab === 'contacts') {
        const ids = getFavoriteIdsByType('attendee');
        const contacts = ids.length === 0
          ? []
          : await fetchAll(ids, (id) => attendeeService.getById(id, eventId));
        if (contactRequestId === contactRequestSequence.current) {
          setFavContactState({ eventId, contacts });
        }
      }
    } catch (err) {
      console.error('Library load error', err);
    } finally {
      if (loadRequestSequence.current === loadRequestId) {
        setLoadingRequestId((current) => (
          current === loadRequestId ? null : current
        ));
      }
    }
  }

  const [exporting, setExporting] = useState(false);

  // Fetch favorited entities of a type on demand (tabs load lazily, so an
  // export shouldn't depend on the user having visited that tab first).
  async function fetchFavs<T>(type: FavoriteEntityType, fetcher: (id: string) => Promise<T | null>): Promise<T[]> {
    const ids = getFavoriteIdsByType(type);
    if (ids.length === 0) return [];
    const settled = await Promise.allSettled(ids.map(fetcher));
    const out: T[] = [];
    settled.forEach((r) => { if (r.status === 'fulfilled' && r.value) out.push(r.value); });
    return out;
  }

  const handleExportEventMarkdown = async () => {
    setExporting(true);
    try {
      const [event, sessions, papers] = await Promise.all([
        currentEventId ? eventService.getById(currentEventId) : Promise.resolve(null),
        currentEventId ? fetchFavs<Session>('session', (id) => sessionService.getById(id, currentEventId)) : Promise.resolve([]),
        currentEventId ? fetchFavs<Paper>('paper', (id) => paperService.getById(id, currentEventId)) : Promise.resolve([]),
      ]);
      const md = buildEventMarkdown({ event, notes, sessions, papers });
      await exportFile({
        filename: `${event?.name ? slug(event.name) : 'conference'}-export.md`,
        content: md,
        mimeType: 'text/markdown',
        dialogTitle: 'Export conference',
      });
      notify.success('Conference exported');
    } catch {
      notify.error('Could not export');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPapers = async (format: 'bibtex' | 'ris') => {
    setExporting(true);
    try {
      const papers = favPapers.length > 0
        ? favPapers
        : currentEventId
          ? await fetchFavs<Paper>('paper', (id) => paperService.getById(id, currentEventId))
          : [];
      if (papers.length === 0) { notify.info('No saved papers to export'); return; }
      const content = format === 'bibtex' ? buildBibTeX(papers) : buildRIS(papers);
      await exportFile({
        filename: `papers.${format === 'bibtex' ? 'bib' : 'ris'}`,
        content,
        mimeType: format === 'bibtex' ? 'application/x-bibtex' : 'application/x-research-info-systems',
        dialogTitle: `Export ${format === 'bibtex' ? 'BibTeX' : 'RIS'}`,
      });
      notify.success(`Exported ${papers.length} paper${papers.length === 1 ? '' : 's'}`);
    } catch {
      notify.error('Could not export papers');
    } finally {
      setExporting(false);
    }
  };

  const handleExportCalendar = async () => {
    setExporting(true);
    try {
      const [event, sessions] = await Promise.all([
        currentEventId ? eventService.getById(currentEventId) : Promise.resolve(null),
        favSessions.length > 0
          ? Promise.resolve(favSessions)
          : currentEventId
            ? fetchFavs<Session>('session', (id) => sessionService.getById(id, currentEventId))
            : Promise.resolve([]),
      ]);
      if (sessions.length === 0) { notify.info('No saved sessions to add'); return; }
      await exportFile({
        filename: `${event?.name ? slug(event.name) : 'agenda'}.ics`,
        content: buildICS(sessions, event),
        mimeType: 'text/calendar',
        dialogTitle: 'Add to calendar',
      });
      notify.success(`${sessions.length} session${sessions.length === 1 ? '' : 's'} → calendar`);
    } catch {
      notify.error('Could not build calendar');
    } finally {
      setExporting(false);
    }
  };

  const handleTabChange = (tab: ModuleNavigationTab) => {
    setSearch('');
    navigate(tab.path, { replace: true });
  };

  const card = {
    backgroundColor: 'var(--app-surface)',
    borderColor: 'var(--app-border)',
    boxShadow: 'var(--app-card-shadow)',
  };

  const q = search.toLowerCase();

  const renderContent = () => {
    if (activeTab !== 'notes' && loadingFav) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
        </div>
      );
    }

    switch (activeTab) {
      case 'notes':
        return (
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                const session = authSession;
                if (!session || !isAuthSessionCurrent(session)) return;
                const newNote = addNote({ title: '', content: '', source: 'manual' }, session);
                if (newNote) navigate(`/notes/${newNote.id}`);
              }}
              className="flex items-center gap-3 rounded-xl p-4 border active:scale-[0.98] transition-transform"
              style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}
            >
              <div className="size-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--app-accent-dim)' }}>
                <Plus className="w-5 h-5" style={{ color: 'var(--app-accent)' }} />
              </div>
              <span className="text-sm" style={{ color: 'var(--app-accent)' }}>Create new note</span>
            </button>

            <button
              onClick={() => navigate('/notes')}
              className="flex items-center justify-center gap-2 rounded-xl p-3 border active:scale-[0.98] transition-transform"
              style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
            >
              <FileText className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
              <span className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>View all notes ({notes.length})</span>
            </button>

            {notes
              .filter((n) => !q || n.title.toLowerCase().includes(q) || n.content.toLowerCase().includes(q))
              .slice(0, 10)
              .map((note) => (
                <button
                  key={note.id}
                  onClick={() => navigate(`/notes/${note.id}`)}
                  className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                  style={card}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm flex-1 truncate" style={{ color: 'var(--app-text)' }}>{note.title || 'Untitled Note'}</p>
                    {note.source !== 'manual' && (
                      <span className="flex items-center gap-1 text-[10px] shrink-0" style={{ color: 'var(--app-accent)' }}>
                        <Sparkles className="w-2.5 h-2.5" />AI
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-1.5 line-clamp-2" style={{ color: 'var(--app-text-muted)' }}>
                    {note.content.split('\n').filter((l) => l.trim() && !l.startsWith('#')).slice(0, 2).join(' ').replace(/[*_#•]/g, '').slice(0, 120) || 'Empty note'}
                  </p>
                  <p className="text-[10px] mt-1.5 flex items-center gap-1" style={{ color: 'var(--app-text-subtle)' }}>
                    <Clock className="w-2.5 h-2.5" />
                    {(() => { try { return new Date(note.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return 'Recent'; } })()}
                  </p>
                </button>
              ))}

            {notes.length === 0 && (
              <div className="flex flex-col items-center py-10 gap-2">
                <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No notes yet</p>
                <p className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>Create a note or let AI generate one during a session</p>
              </div>
            )}
          </div>
        );

      case 'agenda': {
        const filtered = favSessions.filter((s) => !q || s.title.toLowerCase().includes(q));
        if (filtered.length === 0) return <EmptyFavorites label="sessions" />;
        return (
          <div className="flex flex-col gap-3">
            {filtered.map((session) => {
              const fmt = (t: string) => { const [h, m] = t.split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; };
              return (
                <button
                  key={session.id}
                  onClick={() => navigate(`/session/${session.id}`)}
                  className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                  style={card}
                >
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>{session.title}</p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{fmt(session.start_time)}</span>
                    {session.room && <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>· {session.room}</span>}
                    <span className="text-[10px] px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: 'var(--app-tag-bg)', color: 'var(--app-tag-text)' }}>
                      {session.type}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        );
      }

      case 'papers': {
        const filtered = favPapers.filter((p) => !q || p.title.toLowerCase().includes(q));
        if (filtered.length === 0) return <EmptyFavorites label="papers" />;
        return (
          <div className="flex flex-col gap-3">
            {filtered.map((paper) => (
              <button
                key={paper.id}
                onClick={() => navigate(`/paper/${paper.id}`)}
                className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                style={card}
              >
                <p className="text-sm leading-snug" style={{ color: 'var(--app-text)' }}>{paper.title}</p>
                <p className="text-xs mt-1.5" style={{ color: 'var(--app-text-muted)' }}>
                  {Array.isArray(paper.authors) ? paper.authors.join(', ') : paper.authors}
                </p>
              </button>
            ))}
          </div>
        );
      }

      case 'speakers': {
        const filtered = favSpeakers.filter((s) => !q || s.name.toLowerCase().includes(q));
        if (filtered.length === 0) return <EmptyFavorites label="speakers" />;
        return (
          <div className="flex flex-col gap-3">
            {filtered.map((speaker) => (
              <button
                key={speaker.id}
                onClick={() => navigate(`/speaker/${speaker.id}`)}
                className="rounded-xl p-4 border text-left flex items-center gap-4 active:scale-[0.98] transition-transform w-full"
                style={card}
              >
                <div className="size-12 rounded-full overflow-hidden flex items-center justify-center shrink-0 text-lg text-white" style={{ background: 'var(--app-accent-dim)' }}>
                  <AvatarImage source={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover" fallback={<span style={{ color: 'var(--app-accent)' }}>{speaker.name[0]}</span>} />
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>{speaker.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{speaker.title}</p>
                </div>
              </button>
            ))}
          </div>
        );
      }

      case 'exhibitors': {
        const filtered = favExhibitors.filter((e) => !q || e.name.toLowerCase().includes(q));
        if (filtered.length === 0) return <EmptyFavorites label="exhibitors" />;
        return (
          <div className="flex flex-col gap-3">
            {filtered.map((ex) => (
              <button
                key={ex.id}
                onClick={() => navigate(`/exhibitor/${ex.id}`)}
                className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                style={card}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>{ex.name}</p>
                  {ex.booth && (
                    <span className="text-xs px-2 py-0.5 rounded border" style={{ backgroundColor: 'var(--app-muted)', color: 'var(--app-text-muted)', borderColor: 'var(--app-border)' }}>
                      Booth {ex.booth}
                    </span>
                  )}
                </div>
                {ex.category && <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>{ex.category}</p>}
              </button>
            ))}
          </div>
        );
      }

      case 'contacts': {
        const filtered = favContacts.filter((c) => !q || c.name.toLowerCase().includes(q));
        if (filtered.length === 0) return <EmptyFavorites label="contacts" />;
        return (
          <div className="flex flex-col gap-3">
            {filtered.map((contact) => (
              <button
                key={contact.id}
                onClick={() => {
                  if (isEnabled('attendees')) navigate(`/attendee/${contact.id}`);
                }}
                disabled={!isEnabled('attendees')}
                title={isEnabled('attendees') ? `View ${contact.name}` : 'Attendee profiles are not available for this event'}
                className="rounded-xl p-4 border text-left flex items-center gap-4 active:scale-[0.98] transition-transform w-full"
                style={{ ...card, opacity: isEnabled('attendees') ? 1 : 0.75 }}
              >
                <div className="size-12 rounded-full overflow-hidden flex items-center justify-center shrink-0 text-lg text-white" style={{ background: 'var(--app-accent-dim)' }}>
                  <AvatarImage source={contact.avatar_url} alt={contact.name} className="w-full h-full object-cover" fallback={<span style={{ color: 'var(--app-accent)' }}>{contact.name[0]}</span>} />
                </div>
                <div>
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>{contact.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                    {[contact.title, contact.company].filter(Boolean).join(' @ ')}
                  </p>
                </div>
              </button>
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  };

  const chipStyle = {
    backgroundColor: 'var(--app-surface)',
    borderColor: 'var(--app-border)',
    color: 'var(--app-text-muted)',
  };

  const renderExportBar = () => {
    type Chip = { label: string; icon: typeof Download; onClick: () => void };
    let chips: Chip[] = [];
    if (activeTab === 'notes') {
      chips = [{ label: 'Export conference (.md)', icon: Download, onClick: handleExportEventMarkdown }];
    } else if (activeTab === 'agenda') {
      chips = [{ label: 'Add to Calendar (.ics)', icon: CalendarPlus, onClick: handleExportCalendar }];
    } else if (activeTab === 'papers') {
      chips = [
        { label: 'BibTeX', icon: Quote, onClick: () => handleExportPapers('bibtex') },
        { label: 'RIS', icon: Quote, onClick: () => handleExportPapers('ris') },
      ];
    }
    if (chips.length === 0) return null;
    return (
      <div className="flex gap-2 flex-wrap pt-1 pb-3">
        {chips.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.label}
              onClick={c.onClick}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border active:scale-95 transition-transform disabled:opacity-50"
              style={chipStyle}
            >
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
              {c.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="size-full flex flex-col overflow-hidden transition-colors duration-300" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Header */}
      <div className="app-screen-header px-4 pt-12 pb-4 shrink-0" style={{ backgroundColor: 'var(--app-bg)' }}>
        <h1 className="text-2xl mb-4" style={{ color: 'var(--app-text)' }}>My Library</h1>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {tabs.map((tab) => {
            const Icon = TAB_ICONS[tab.id as keyof typeof TAB_ICONS];
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab)}
                className="flex-none min-w-0 max-w-36 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs transition-colors"
                title={tab.label}
                style={
                  active
                    ? { backgroundColor: 'var(--app-accent-solid)', color: '#fff' }
                    : { backgroundColor: 'var(--app-surface)', color: 'var(--app-text-muted)', border: '1px solid var(--app-border)' }
                }
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-3 rounded-lg text-sm outline-none transition-colors"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
              color: 'var(--app-text)',
            }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="app-nav-content flex-1 overflow-y-auto px-4">
        {renderExportBar()}
        {renderContent()}
      </div>

      <BottomNav />
    </div>
  );
}
